import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from '@/features/auth/AuthContext';
import {
  DashboardLayouts,
  ViewModeLayouts,
  useDashboardLayoutService
} from '@/features/dashboard/services/dashboardLayoutService';
import type { ViewModeWidgetData, Widget } from '@/features/dashboard/services/dashboardLayoutTypes';
import type { DashboardWidget } from '@/features/dashboard/DashboardGrid';
import {
  DEFAULT_WIDGET_CONFIGS,
  WIDGET_LAYOUT_DEFINITIONS,
  type DashboardViewMode,
} from '@/features/dashboard/components/widgetRegistryMetadata';
import { generateAutoLayout } from '@/features/dashboard/utils/layoutHelpers';
import { GRID_CONFIG } from '@/features/dashboard/constants';
import { getDashboardStorageScope, scopedDashboardKey } from '../dashboardStorageScope';
import { buildWidgetDataForSave } from '../services/dashboardLayoutTransformers';
import { placeWidgetInLayouts, type WidgetPlacement } from '../utils/widgetPlacement';
import { log } from '@/shared/utils/logger';
import i18n from '@/shared/i18n/config';
import { canOpenWorkspaceView } from '@/shared/workspaceViewAccess';

// Context types
interface DashboardLayoutContextType {
  // Current layouts and widget data
  layouts: ViewModeLayouts;
  widgetData?: ViewModeWidgetData;
  currentViewMode: string;
  
  // One operation identity spans grid events, visibility edits, reset and restore.
  operationToken: object;
  queueLayout: (viewMode: string, layouts: DashboardLayouts, token: object) => void;
  changeWidgetVisibility: (viewMode: string, id: string, visible: boolean, placement?: WidgetPlacement) => boolean;
  // Layout operations
  saveLayout: (viewMode: string, layouts: DashboardLayouts, widgetData?: Widget[]) => Promise<boolean>;
  resetLayout: (viewMode: string) => Promise<boolean>;
  getLayoutForViewMode: (viewMode: string) => DashboardLayouts | null;
  loadLayouts: () => Promise<void>;
  loadLayout: (viewMode: string, layouts: DashboardLayouts, widgets?: Widget[]) => void;
  
  // Sync status
  syncStatus: 'synced' | 'pending' | 'offline';
  hasPendingChanges: boolean;
  syncPendingChanges: () => Promise<void>;
  
  // Loading states
  isLoading: boolean;
  error: string | null;
}

const DashboardLayoutContext = createContext<DashboardLayoutContextType | undefined>(undefined);

// Default layouts for different view modes
const getDefaultLayouts = (viewMode: string = 'client'): DashboardLayouts => {
  const mode: DashboardViewMode = viewMode === 'admin' || viewMode === 'employee' || viewMode === 'client'
    ? viewMode
    : 'client';
  const defaultWidgetIds = DEFAULT_WIDGET_CONFIGS[mode] || DEFAULT_WIDGET_CONFIGS.client;
  const availableWidgets = new Map(
    WIDGET_LAYOUT_DEFINITIONS
      .filter((widget) => widget.availableFor.includes(mode))
      .map((widget) => [widget.id, widget]),
  );
  const widgets = defaultWidgetIds.reduce<DashboardWidget[]>((acc, widgetId) => {
    const definition = availableWidgets.get(widgetId);
    if (!definition) {
      return acc;
    }

    acc.push({
      id: definition.id,
      component: null,
      defaultSize: definition.defaultSize,
      minSize: definition.minSize,
      maxSize: definition.maxSize,
      deletable: definition.deletable,
      title: definition.title
    });
    return acc;
  }, []);

  return {
    lg: generateAutoLayout(widgets, GRID_CONFIG.COLS.lg),
    md: generateAutoLayout(widgets, GRID_CONFIG.COLS.md),
    sm: generateAutoLayout(widgets, GRID_CONFIG.COLS.sm),
    xs: generateAutoLayout(widgets, GRID_CONFIG.COLS.xs),
    xxs: generateAutoLayout(widgets, GRID_CONFIG.COLS.xxs)
  };
};

interface DashboardLayoutProviderProps {
  children: ReactNode;
}

export const DashboardLayoutProvider: React.FC<DashboardLayoutProviderProps> = ({ children }) => {
  const { user, isAuthenticated, hasPermission } = useAuth();
  // The provider wraps every view; an account that may not open the Dashboard has no layouts to load.
  const canUseDashboard = isAuthenticated && Boolean(user) && canOpenWorkspaceView('dashboard', hasPermission);
  const layoutService = useDashboardLayoutService();
  const scopeKey = `${isAuthenticated ? user?.id || '' : ''}:${getDashboardStorageScope()}:${user?.role || ''}`;
  type Snapshot = { layouts: ViewModeLayouts; widgetData?: ViewModeWidgetData };
  const sessionRef = useRef({ key: scopeKey });
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const snapshotRef = useRef<Snapshot>({ layouts: {} });
  const tokenRef = useRef<object>({});
  const [operationToken, setOperationToken] = useState(tokenRef.current);
  const [snapshot, setSnapshot] = useState<Snapshot>(snapshotRef.current);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<'synced' | 'pending' | 'offline'>('synced');
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const requestRef = useRef(0);
  const editRef = useRef(0);
  const modeGenerationsRef = useRef(new Map<string, number>());
  const modeEditsRef = useRef(new Map<string, number>());
  const tokenGenerationsRef = useRef(new WeakMap<object, Map<string, number>>());
  if (!tokenGenerationsRef.current.has(tokenRef.current)) {
    tokenGenerationsRef.current.set(tokenRef.current, new Map(modeGenerationsRef.current));
  }
  const resettingRef = useRef(new Map<string, object>());
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const pendingRef = useRef(new Map<string, { layouts: DashboardLayouts; widgets: Widget[]; token: object }>());
  const flushRef = useRef<(mode: string) => void>(() => {});

  const cancelQueue = useCallback((mode?: string) => {
    const modes = mode === undefined ? [...timersRef.current.keys()] : [mode];
    for (const key of modes) {
      const timer = timersRef.current.get(key);
      if (timer !== undefined) clearTimeout(timer);
      timersRef.current.delete(key);
      pendingRef.current.delete(key);
    }
    if (mode === undefined) pendingRef.current.clear();
  }, []);
  // Render-time identity change also fences A -> B -> A late completions.
  if (sessionRef.current.key !== scopeKey) {
    sessionRef.current = { key: scopeKey };
    cancelQueue();
    resettingRef.current.clear();
    modeGenerationsRef.current.clear();
    modeEditsRef.current.clear();
    tokenGenerationsRef.current = new WeakMap();
    requestRef.current++;
    tokenRef.current = {};
    tokenGenerationsRef.current.set(tokenRef.current, new Map());
    snapshotRef.current = { layouts: {} };
    setSnapshot(snapshotRef.current);
    setOperationToken(tokenRef.current);
    setError(null);
  }
  const session = sessionRef.current;
  const isSessionCurrent = useCallback(() => mountedRef.current && sessionRef.current === session, [session]);
  const publish = useCallback((next: Snapshot) => {
    snapshotRef.current = next;
    setSnapshot(next);
  }, []);
  const updateStatus = useCallback(() => {
    setSyncStatus(layoutService.getSyncStatus());
    setHasPendingChanges(layoutService.hasPendingChanges());
  }, [layoutService]);
  const isModeTokenCurrent = useCallback((mode: string, token: object) => {
    const generations = tokenGenerationsRef.current.get(token);
    return generations !== undefined && (generations.get(mode) ?? 0) === (modeGenerationsRef.current.get(mode) ?? 0);
  }, []);
  const replaceOperation = useCallback((mode: string) => {
    cancelQueue(mode);
    modeGenerationsRef.current.set(mode, (modeGenerationsRef.current.get(mode) ?? 0) + 1);
    requestRef.current++;
    editRef.current++;
    tokenRef.current = {};
    tokenGenerationsRef.current.set(tokenRef.current, new Map(modeGenerationsRef.current));
    setOperationToken(tokenRef.current);
    return tokenRef.current;
  }, [cancelQueue]);
  const retainVisibility = useCallback((mode: string, widgets: Widget[]) => {
    try {
      localStorage.setItem(scopedDashboardKey(`dashboard-preferences-${mode}`), JSON.stringify({
        activeWidgetIds: widgets.filter(w => w.is_visible ?? w.isVisible ?? true).map(w => w.id), viewMode: mode,
      }));
    } catch (err) { log.error('DashboardLayoutContext', 'Failed to retain visibility preferences', err); }
  }, []);
  const publishMode = useCallback((mode: string, layouts: DashboardLayouts, widgets?: Widget[]) => {
    const previous = snapshotRef.current;
    if (widgets) retainVisibility(mode, widgets);
    publish({
      layouts: { ...previous.layouts, [mode]: layouts },
      widgetData: widgets === undefined ? previous.widgetData : { ...previous.widgetData, [mode]: widgets },
    });
  }, [publish, retainVisibility]);

  const loadLayouts = useCallback(async () => {
    if (!isSessionCurrent()) return;
    const request = ++requestRef.current;
    const edit = editRef.current;
    const current = () => isSessionCurrent() && request === requestRef.current && edit === editRef.current;
    setIsLoading(true);
    try {
      const loaded = await layoutService.getLayouts();
      if (current()) {
        for (const mode of ['admin', 'client', 'employee'] as const) {
          if (loaded.widgetData?.[mode]) continue;
          try {
            const preferences = JSON.parse(localStorage.getItem(scopedDashboardKey(`dashboard-preferences-${mode}`)) || 'null');
            if (Array.isArray(preferences?.activeWidgetIds) && preferences.activeWidgetIds.every((id: unknown) => typeof id === 'string')) {
              const definitions = WIDGET_LAYOUT_DEFINITIONS.filter(w => w.availableFor.includes(mode));
              loaded.widgetData = { ...loaded.widgetData, [mode]: buildWidgetDataForSave(loaded.layouts[mode] || getDefaultLayouts(mode), definitions, preferences.activeWidgetIds) };
            }
          } catch (err) { log.error('DashboardLayoutContext', 'Failed to read scoped visibility preferences', err); }
        }
        publish(loaded); setError(null);
      }
    } catch (err) {
      if (current()) setError(err instanceof Error ? err.message : i18n.t('Failed to load layouts'));
    } finally {
      if (isSessionCurrent() && request === requestRef.current) setIsLoading(false);
    }
  }, [layoutService, isSessionCurrent, publish]);

  const saveLayout = useCallback(async (mode: string, layouts: DashboardLayouts, widgets?: Widget[]): Promise<boolean> => {
    if (!isSessionCurrent() || resettingRef.current.has(mode)) return false;
    cancelQueue(mode);
    const token = tokenRef.current;
    editRef.current++;
    const edit = (modeEditsRef.current.get(mode) ?? 0) + 1;
    modeEditsRef.current.set(mode, edit);
    // The UI owns the latest intent even when its remote save fails.
    publishMode(mode, layouts, widgets);
    try {
      const success = await layoutService.saveLayout(mode, layouts, widgets);
      if (!isSessionCurrent() || !isModeTokenCurrent(mode, token) || edit !== modeEditsRef.current.get(mode)) return false;
      setError(success ? null : i18n.t('Unable to save {{mode}} layout. Please try again.', { mode: i18n.t(mode) }));
      updateStatus();
      return success;
    } catch (err) {
      if (isSessionCurrent() && isModeTokenCurrent(mode, token) && edit === modeEditsRef.current.get(mode)) {
        setError(err instanceof Error ? err.message : i18n.t('Failed to save layout'));
        updateStatus();
      }
      return false;
    }
  }, [isSessionCurrent, layoutService, publishMode, updateStatus, cancelQueue, isModeTokenCurrent]);

  const queueSnapshot = useCallback((mode: string, layouts: DashboardLayouts, widgets: Widget[], token: object) => {
    if (!isSessionCurrent() || !isModeTokenCurrent(mode, token) || resettingRef.current.has(mode)) return;
    editRef.current++;
    modeEditsRef.current.set(mode, (modeEditsRef.current.get(mode) ?? 0) + 1);
    // No state-updater side effects: stage once and keep geometry + visibility together.
    layoutService.stageLayout(mode, layouts, widgets);
    publishMode(mode, layouts, widgets);
    updateStatus();
    pendingRef.current.set(mode, { layouts, widgets, token });
    const timer = timersRef.current.get(mode);
    if (timer !== undefined) clearTimeout(timer);
    timersRef.current.set(mode, setTimeout(() => { timersRef.current.delete(mode); flushRef.current(mode); }, 250));
  }, [isSessionCurrent, layoutService, publishMode, updateStatus, isModeTokenCurrent]);
  flushRef.current = (mode: string) => {
    const pending = pendingRef.current.get(mode);
    pendingRef.current.delete(mode);
    if (pending && isModeTokenCurrent(mode, pending.token)) void saveLayout(mode, pending.layouts, pending.widgets);
  };

  const queueLayout = useCallback((mode: string, incoming: DashboardLayouts, token: object) => {
    const previous = snapshotRef.current;
    const oldWidgets = previous.widgetData?.[mode];
    const active = oldWidgets
      ? oldWidgets.filter(w => w.is_visible ?? w.isVisible ?? true).map(w => w.id)
      : DEFAULT_WIDGET_CONFIGS[mode as DashboardViewMode] || [];
    const layouts = { ...incoming };
    for (const breakpoint of ['lg', 'md', 'sm', 'xs', 'xxs'] as const) {
      const ids = new Set(incoming[breakpoint].map(item => item.i));
      layouts[breakpoint] = [...incoming[breakpoint], ...(previous.layouts[mode]?.[breakpoint] || []).filter(item => !ids.has(item.i))];
    }
    const definitions = WIDGET_LAYOUT_DEFINITIONS.filter(w => w.availableFor.includes(mode as DashboardViewMode));
    queueSnapshot(mode, layouts, buildWidgetDataForSave(layouts, definitions, active, oldWidgets), token);
  }, [queueSnapshot]);

  const changeWidgetVisibility = useCallback((mode: string, id: string, visible: boolean, placement?: WidgetPlacement) => {
    if (!isSessionCurrent() || resettingRef.current.has(mode)) return false;
    const definitions = WIDGET_LAYOUT_DEFINITIONS.filter(w => w.availableFor.includes(mode as DashboardViewMode));
    const definition = definitions.find(w => w.id === id);
    if (!definition) return false;
    const previous = snapshotRef.current;
    const oldWidgets = previous.widgetData?.[mode];
    const active = new Set(oldWidgets
      ? oldWidgets.filter(w => w.is_visible ?? w.isVisible ?? true).map(w => w.id)
      : DEFAULT_WIDGET_CONFIGS[mode as DashboardViewMode] || []);
    if (active.has(id) === visible) return true;
    let layouts = previous.layouts[mode] || getDefaultLayouts(mode);
    if (visible) {
      const placed = placeWidgetInLayouts(layouts, { ...definition, component: null }, active, placement);
      if (!placed) { setError(i18n.t('No space available for this widget in the dashboard.')); return false; }
      layouts = placed;
      active.add(id);
    } else active.delete(id);
    const widgets = buildWidgetDataForSave(layouts, definitions, active, oldWidgets);
    queueSnapshot(mode, layouts, widgets, tokenRef.current);
    return true;
  }, [isSessionCurrent, queueSnapshot]);

  const resetLayout = useCallback(async (mode: string): Promise<boolean> => {
    if (!isSessionCurrent() || resettingRef.current.has(mode)) return false;
    const token = replaceOperation(mode);
    resettingRef.current.set(mode, token);
    setIsLoading(false);
    try {
      const success = await layoutService.resetLayout(mode);
      if (!isSessionCurrent() || !isModeTokenCurrent(mode, token)) return false;
      if (success) {
        const next = { layouts: { ...snapshotRef.current.layouts }, widgetData: { ...snapshotRef.current.widgetData } };
        delete next.layouts[mode];
        const definitions = WIDGET_LAYOUT_DEFINITIONS.filter(w => w.availableFor.includes(mode as DashboardViewMode));
        next.widgetData[mode] = buildWidgetDataForSave(getDefaultLayouts(mode), definitions, DEFAULT_WIDGET_CONFIGS[mode as DashboardViewMode] || []);
        retainVisibility(mode, next.widgetData[mode]);
        publish(next);
        setError(null);
        replaceOperation(mode);
      } else setError(i18n.t('Failed to reset layout for {{mode}}', { mode: i18n.t(mode) }));
      updateStatus();
      return success;
    } catch (err) {
      if (isSessionCurrent() && isModeTokenCurrent(mode, token)) setError(err instanceof Error ? err.message : i18n.t('Failed to reset layout'));
      return false;
    } finally {
      if (resettingRef.current.get(mode) === token) resettingRef.current.delete(mode);
    }
  }, [isSessionCurrent, layoutService, publish, replaceOperation, updateStatus, retainVisibility, isModeTokenCurrent]);

  const loadLayout = useCallback((mode: string, layouts: DashboardLayouts, widgets?: Widget[]) => {
    if (!isSessionCurrent() || resettingRef.current.has(mode)) return;
    replaceOperation(mode);
    // Named restores are recoverable locally before their remote acknowledgement.
    layoutService.stageLayout(mode, layouts, widgets);
    publishMode(mode, layouts, widgets);
    setIsLoading(false);
    setError(null);
    updateStatus();
  }, [isSessionCurrent, replaceOperation, layoutService, publishMode, updateStatus]);

  const syncPendingChanges = useCallback(async () => {
    if (!isSessionCurrent() || resettingRef.current.size > 0) return;
    cancelQueue();
    const token = tokenRef.current;
    try {
      await layoutService.syncPendingChanges();
      if (isSessionCurrent() && token === tokenRef.current) { updateStatus(); await loadLayouts(); }
    } catch (err) {
      if (isSessionCurrent() && token === tokenRef.current) setError(err instanceof Error ? err.message : i18n.t('Failed to sync layout'));
    }
  }, [isSessionCurrent, cancelQueue, layoutService, updateStatus, loadLayouts]);

  useEffect(() => {
    const requestCounter = requestRef;
    if (canUseDashboard) {
      void loadLayouts();
    } else {
      layoutService.clearCache();
    }
    return () => { cancelQueue(); requestCounter.current++; };
  }, [session, canUseDashboard, loadLayouts, layoutService, isSessionCurrent, cancelQueue]);
  useEffect(() => {
    updateStatus();
    const interval = setInterval(updateStatus, 5000);
    const online = () => { if (layoutService.hasPendingChanges()) void syncPendingChanges(); else updateStatus(); };
    window.addEventListener('online', online);
    window.addEventListener('offline', updateStatus);
    return () => { clearInterval(interval); window.removeEventListener('online', online); window.removeEventListener('offline', updateStatus); };
  }, [layoutService, syncPendingChanges, updateStatus]);

  const getLayoutForViewMode = useCallback((mode: string) => snapshot.layouts[mode] || null, [snapshot]);
  const contextValue: DashboardLayoutContextType = {
    ...snapshot, currentViewMode: user?.role === 'partner' ? 'client' : user?.role || 'client',
    operationToken, queueLayout, changeWidgetVisibility, saveLayout, resetLayout, getLayoutForViewMode,
    loadLayouts, loadLayout, syncStatus, hasPendingChanges, syncPendingChanges, isLoading, error,
  };
  return <DashboardLayoutContext.Provider value={contextValue}>{children}</DashboardLayoutContext.Provider>;
};

// Hook to use the dashboard layout context
// eslint-disable-next-line react-refresh/only-export-components
export const useDashboardLayout = (): DashboardLayoutContextType => {
  const context = useContext(DashboardLayoutContext);
  if (context === undefined) {
    throw new Error('useDashboardLayout must be used within a DashboardLayoutProvider');
  }
  return context;
};

// Helper hook to get layouts with defaults
// eslint-disable-next-line react-refresh/only-export-components
export const useLayoutsWithDefaults = () => {
  const { currentViewMode, getLayoutForViewMode } = useDashboardLayout();
  
  const getCurrentLayouts = useCallback((): DashboardLayouts => {
    const userLayouts = getLayoutForViewMode(currentViewMode);
    return userLayouts || getDefaultLayouts(currentViewMode);
  }, [getLayoutForViewMode, currentViewMode]);
  
  const getLayoutsForViewMode = useCallback((viewMode: string): DashboardLayouts => {
    const userLayouts = getLayoutForViewMode(viewMode);
    return userLayouts || getDefaultLayouts(viewMode);
  }, [getLayoutForViewMode]);
  
  return {
    getCurrentLayouts,
    getLayoutsForViewMode,
    currentViewMode,
    hasCustomLayout: (viewMode: string) => !!getLayoutForViewMode(viewMode)
  };
};
